import React, { useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { typeRole, typeRoleStyleVars } from '../../theme/typeRoles';

// Live specimen of a public page built from the dashboard's unsaved values.
// The frame DEFINES the --jet-type-* variables inline (typeRoleStyleVars) and
// every element reads them via typeRole(), i.e. the exact contract the public
// site uses. The frame renders at a real device width (1280 / 390px) and is
// scaled down to fit the panel so proportions stay true.

const FRAME_WIDTH = { desktop: 1280, mobile: 390 };
const GOLD = '#b89b5e';
const DARK = '#171a21';
const NAVY = '#13334D';
const INK = '#212b36';

const t = (role) => typeRole(role);
const reset = { m: 0, fontFamily: 'inherit' };

const btn = {
  ...reset,
  border: 0,
  borderRadius: '4px',
  px: 3,
  py: 1.25,
  cursor: 'default',
  display: 'inline-block',
};

export default function TypeSpecimenPreview({ roles, defaults, device = 'desktop', fit = true }) {
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const [box, setBox] = useState({ scale: 1, height: undefined, left: 0 });
  const frameWidth = FRAME_WIDTH[device] || FRAME_WIDTH.desktop;
  const isMobile = device === 'mobile';

  useLayoutEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return undefined;
    // Ignores sub-pixel/1% changes so a scrollbar toggling in a parent can't
    // bounce the scale back and forth (the parent should also reserve its
    // scrollbar gutter).
    const measure = () => {
      if (outer.clientWidth <= 0) return;
      const scale = fit ? Math.min(1, outer.clientWidth / frameWidth) : 1;
      const next = {
        scale,
        height: inner.offsetHeight * scale,
        left: Math.max(0, (outer.clientWidth - frameWidth * scale) / 2),
      };
      setBox((cur) =>
        Math.abs(cur.scale - next.scale) < 0.01 &&
        Math.abs((cur.height || 0) - next.height) < 1 &&
        Math.abs(cur.left - next.left) < 1
          ? cur
          : next
      );
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(outer);
    ro.observe(inner);
    return () => ro.disconnect();
  }, [frameWidth, fit]);

  const pad = isMobile ? 2.5 : 6;

  return (
    <Box ref={outerRef} sx={{ width: '100%', height: box.height, position: 'relative', overflow: 'hidden' }}>
      <Box
        ref={innerRef}
        style={typeRoleStyleVars(roles, device, defaults)}
        sx={{
          position: 'absolute',
          top: 0,
          left: box.left,
          width: frameWidth,
          transform: `scale(${box.scale})`,
          transformOrigin: 'top left',
          bgcolor: '#fff',
          color: INK,
          border: '1px solid',
          borderColor: 'divider',
          fontFamily: 'Public Sans, sans-serif',
        }}
      >
        {/* Utility bar */}
        <Box
          sx={{
            bgcolor: '#0f1116',
            color: 'rgba(255,255,255,0.75)',
            px: pad,
            py: 0.75,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 3,
            ...t('nav-utility'),
          }}
        >
          <span>833-270-7191</span>
          {!isMobile && <span>Make a payment</span>}
          <span>Borrower login</span>
        </Box>

        {/* Main navigation */}
        <Box sx={{ bgcolor: DARK, color: '#fff', px: pad, py: 2, display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{ mr: 'auto', fontWeight: 800, letterSpacing: 1, fontSize: 18 }}>JET HOMELOANS</Box>
          {!isMobile &&
            ['Purchase', 'Refinance', 'Loan programs', 'Find a loan officer'].map((label) => (
              <Box key={label} component="span" sx={t('nav-main')}>
                {label}
              </Box>
            ))}
          {isMobile && <Box sx={{ fontSize: 24, lineHeight: 1 }}>☰</Box>}
        </Box>
        {isMobile && (
          <Box sx={{ bgcolor: '#22262f', color: '#fff', px: pad, py: 1 }}>
            {['Purchase', 'Refinance', 'Loan programs'].map((label) => (
              <Box key={label} sx={{ py: 1, borderBottom: '1px solid rgba(255,255,255,0.08)', ...t('nav-main') }}>
                {label}
              </Box>
            ))}
          </Box>
        )}

        {/* Hero */}
        <Box sx={{ bgcolor: NAVY, color: '#fff', textAlign: 'center', px: pad, py: isMobile ? 5 : 8 }}>
          <Box component="h1" sx={{ ...reset, ...t('hero-headline') }}>
            Home loans made simple
          </Box>
          <Box component="p" sx={{ ...reset, mt: 1.5, mb: 3, ...t('hero-subheadline') }}>
            Fast pre-approvals from local loan officers who know your market.
          </Box>
          <Box component="span" sx={{ ...btn, bgcolor: GOLD, color: '#fff', ...t('hero-cta') }}>
            Apply now
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ px: pad, py: 5 }}>
          <Box sx={{ ...t('h5-h6'), color: GOLD }}>Eyebrow label · H5/H6</Box>
          <Box component="h1" sx={{ ...reset, mt: 1, mb: 2, ...t('h1') }}>
            Page title · H1
          </Box>
          <Box component="p" sx={{ ...reset, mb: 2, ...t('body') }}>
            Body text. Whether you&apos;re buying your first home or refinancing the one you love, our loan
            officers walk you through every step — from pre-approval to closing day — with clear answers and
            no surprises.
          </Box>

          <Box component="h2" sx={{ ...reset, mt: 4, mb: 1.5, ...t('h2') }}>
            Section header · H2
          </Box>
          <Box component="h3" sx={{ ...reset, mb: 1, ...t('h3') }}>
            Subsection header · H3
          </Box>
          <Box component="p" sx={{ ...reset, mb: 3, ...t('body') }}>
            Compare fixed and adjustable rates, estimate your payment, and see which programs fit your goals.
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 2, mb: 3 }}>
            {['Conventional loans', 'VA loans', 'FHA loans'].map((title) => (
              <Box key={title} sx={{ border: '1px solid #e5e8eb', borderRadius: 2, p: 2.5 }}>
                <Box component="h4" sx={{ ...reset, mb: 1, ...t('h4') }}>
                  {title} · H4
                </Box>
                <Box component="p" sx={{ ...reset, ...t('body-small'), color: '#637381' }}>
                  Small body text for card descriptions and supporting details.
                </Box>
              </Box>
            ))}
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
            <Box component="span" sx={{ ...btn, bgcolor: GOLD, color: '#fff', ...t('button') }}>
              Learn more
            </Box>
            <Box component="span" sx={{ ...btn, bgcolor: 'transparent', color: NAVY, border: `2px solid ${NAVY}`, ...t('button') }}>
              Contact us
            </Box>
          </Box>

          <Box component="p" sx={{ ...reset, mb: 4, color: '#637381', ...t('body-small') }}>
            Small body · Rates and programs subject to change. Not all products are available in all states.
          </Box>

          {/* Form */}
          <Box sx={{ maxWidth: 420, border: '1px solid #e5e8eb', borderRadius: 2, p: 2.5 }}>
            {[
              { label: 'Email address', value: 'jordan@example.com', helper: 'We’ll never share your email.' },
              { label: 'Phone number', value: '904-555-01', helper: 'Enter a valid 10-digit phone number', error: true },
            ].map((f) => (
              <Box key={f.label} sx={{ mb: 2 }}>
                <Box component="label" sx={{ display: 'block', mb: 0.75, ...t('form-label') }}>
                  {f.label}
                </Box>
                <Box
                  sx={{
                    border: `1px solid ${f.error ? '#d32f2f' : '#c4cdd5'}`,
                    borderRadius: 1,
                    px: 1.75,
                    py: 1.25,
                    ...t('form-input'),
                  }}
                >
                  {f.value}
                </Box>
                <Box sx={{ mt: 0.5, color: f.error ? '#d32f2f' : '#637381', ...t('form-helper') }}>{f.helper}</Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ bgcolor: DARK, color: 'rgba(255,255,255,0.88)', px: pad, pt: 4, pb: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 3, mb: 3 }}>
            {[
              ['Corporate', ['14701 Philips Highway, Suite 202', 'Jacksonville, FL 32256', '833-270-7191']],
              ['Legal', ['Privacy Policy', 'Legal & Licensing', 'Terms of Use']],
              ['Licensing', ['Equal Housing Lender', 'www.nmlsconsumeraccess.org']],
            ].map(([heading, items]) => (
              <Box key={heading}>
                <Box sx={{ color: '#fff', mb: 1.5, ...t('footer-heading') }}>{heading}</Box>
                {items.map((item) => (
                  <Box key={item} sx={{ mb: 0.75, ...t('footer-text') }}>
                    {item}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
          <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.12)', pt: 2, color: 'rgba(255,255,255,0.78)', ...t('footer-legal') }}>
            ©Jet HomeLoans, LP NMLS ID# 1660135. Equal Housing Lender. All products are subject to credit and
            property approval. This is not a commitment to lend.
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

TypeSpecimenPreview.propTypes = {
  roles: PropTypes.object,
  defaults: PropTypes.object,
  device: PropTypes.oneOf(['desktop', 'mobile']),
  fit: PropTypes.bool,
};
